import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Box,
    Chip,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Snackbar,
    Alert,
    Typography,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import VideoLibrarySelection from './VideoLibrarySelection';

const PatientForm = ({ open, onClose, onSubmit, onPatientAdded, editPatient = null }) => {
    const { t } = useTranslation();  // Translation hook

    const isEditMode = Boolean(editPatient);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        durationValue: '',
        durationType: 'weeks',
    });
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [videoLibraryOpen, setVideoLibraryOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // state for checking the change in the form field
    const [changedFields, setChangedFields] = useState({});

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Populate form with edit data when in edit mode
    useEffect(() => {
        if (editPatient && open) {
            let durationType = 'weeks';
            let durationValue = editPatient.numberOfWeeks || 0;

            if (editPatient.numberOfMonths > 0) {
                durationType = 'months';
                durationValue = editPatient.numberOfMonths;
            } else if (editPatient.numberOfWeeks > 0) {
                durationType = 'weeks';
                durationValue = editPatient.numberOfWeeks;
            }

            setFormData({
                firstName: editPatient.firstName || '',
                lastName: editPatient.lastName || '',
                email: editPatient.userEmail || '',
                durationValue: durationValue.toString(),
                durationType: durationType,
            });

            if (editPatient.videos && editPatient.videos.length > 0) {
                setSelectedExercises(editPatient.videos);
            }
        }
    }, [editPatient, open]);

    const handleChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value,
        });
        // checking changed fields
        setChangedFields(prev => ({ ...prev, [field]: true }));
    };

    // Auto-convert weeks to months when > 12
    useEffect(() => {
        const duration = parseInt(formData.durationValue);

        if (formData.durationType === 'weeks' && duration > 12 && !isEditMode) {
            const months = Math.floor(duration / 4);
            setFormData(prev => ({
                ...prev,
                durationValue: months.toString(),
                durationType: 'months',
            }));
        }
    }, [formData.durationValue, formData.durationType, isEditMode]);

    const handleExercisesConfirm = (exercises) => {
        setSelectedExercises(exercises);
    };

    const handleRemoveExercise = (exerciseId) => {
        setSelectedExercises((prev) =>
            prev.filter((exercise) => exercise.id !== exerciseId)
        );
    };

    const resetForm = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            durationValue: '',
            durationType: 'weeks',
        });
        setSelectedExercises([]);
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.email || !formData.firstName || !formData.lastName || !formData.durationValue) {
            setSnackbarMessage(t('pleaseFillAllFields', 'Please fill in all required fields'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            setSnackbarMessage(t('invalidEmail'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (selectedExercises.length === 0) {
            setSnackbarMessage(t('pleaseSelectExercise', 'Please select at least one exercise'));
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        setIsSubmitting(true);

        try {
            const durationNum = parseInt(formData.durationValue) || 0;

            const payload = {
                userFirstName: formData.firstName,
                userLastName: formData.lastName,
                userEmail: formData.email,
                videoIds: selectedExercises.map(exercise => exercise.id),
                numberOfDays: 0,
                numberOfWeeks: formData.durationType === 'weeks' ? durationNum : durationNum * 4,
                numberOfMonths: formData.durationType === 'months' ? durationNum : Math.floor(durationNum / 4),
            };

            const response = await axios.post(
                `${process.env.REACT_APP_FIREBASE_API_HOST}/api/physio`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true,
                }
            );

            const { status } = response.data;
            let message = '';
            let severity = 'success';

            switch (status) {
                case 'NEW_USER':
                    message = isEditMode
                        ? t('patientUpdated', 'Patient updated successfully!')
                        : t('newPatientCreated', 'New patient created successfully! Welcome email sent.');
                    severity = 'success';
                    break;
                case 'USER_SETTINGS_OFF':
                    message = isEditMode
                        ? t('patientUpdatedNotificationsOff', 'Patient updated, but their notification settings are off.')
                        : t('patientAddedNotificationsOff', 'Patient added, but their notification settings are off.');
                    severity = 'warning';
                    break;
                case 'NO_VIDEOS_FOUND':
                    message = t('videosNotFound', 'Error: Selected videos not found.');
                    severity = 'error';
                    break;
                case 'SUCCESS':
                    message = isEditMode
                        ? t('patientInfoUpdated', 'Patient information updated successfully!')
                        : t('exercisesSent', 'Exercises sent successfully to patient!');
                    severity = 'success';
                    break;
                default:
                    message = isEditMode
                        ? t('patientUpdatedUnexpected', 'Patient updated with unexpected status.')
                        : t('patientAddedUnexpected', 'Patient added with unexpected status.');
                    severity = 'info';
            }

            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
            setSnackbarOpen(true);

            if (onSubmit) {
                onSubmit({
                    ...formData,
                    exercises: selectedExercises,
                    numberOfWeeks: payload.numberOfWeeks,
                    numberOfMonths: payload.numberOfMonths,
                });
            }

            if (severity === 'success' && onPatientAdded) {
                resetForm();
                onPatientAdded(Object.keys(changedFields));
                setTimeout(() => {
                    onClose();
                }, 1500);
            }

        } catch (error) {
            console.error('Error saving patient:', error);

            let errorMessage = isEditMode
                ? t('failedUpdatePatient', 'Failed to update patient. Please try again.')
                : t('failedCreatePatient', 'Failed to create patient. Please try again.');

            if (error.response) {
                errorMessage = error.response.data?.message || errorMessage;
            } else if (error.request) {
                errorMessage = t('networkError', 'Network error. Please check your connection.');
            }

            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={isSubmitting ? undefined : onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: '#1d293d',
                        borderRadius: 2,
                        p: 2,
                    },
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        color: 'white',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        pb: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {isEditMode ? t('editPatient', 'Edit Patient') : t('addNewPatient', 'Add New Patient')}
                    <IconButton
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                                color: 'white',
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.3)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* Form Content */}
                <DialogContent sx={{ pb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* First Name */}
                        <Box>
                            <label
                                style={{
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    marginBottom: '8px',
                                    display: 'block',
                                }}
                            >
                                {t('firstName', 'First Name')} *
                            </label>
                            <TextField
                                fullWidth
                                required
                                placeholder={t('firstNamePlaceholder', 'Max')}
                                value={formData.firstName}
                                onChange={handleChange('firstName')}
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#314158',
                                        borderRadius: 1,
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2563EB',
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: '#2a3a4d',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Last Name */}
                        <Box>
                            <label
                                style={{
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    marginBottom: '8px',
                                    display: 'block',
                                }}
                            >
                                {t('lastName', 'Last Name')} *
                            </label>
                            <TextField
                                fullWidth
                                required
                                placeholder={t('lastNamePlaceholder', 'Mustermann')}
                                value={formData.lastName}
                                onChange={handleChange('lastName')}
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#314158',
                                        borderRadius: 1,
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2563EB',
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: '#2a3a4d',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Email */}
                        <Box>
                            <label
                                style={{
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    marginBottom: '8px',
                                    display: 'block',
                                }}
                            >
                                {t('email', 'Email')} *
                            </label>
                            <TextField
                                fullWidth
                                required
                                type="email"
                                placeholder={t('emailPlaceholder', 'max@example.com')}
                                value={formData.email}
                                onChange={handleChange('email')}
                                disabled={isSubmitting || isEditMode}
                                error={formData.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}
                                helperText={
                                    formData.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                                        ? t('invalidEmail')
                                        : ''
                                }
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#314158',
                                        borderRadius: 1,
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2563EB',
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: '#2a3a4d',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            opacity: 1,
                                        },
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: '#f87171',
                                    },
                                }}
                            />
                            {isEditMode && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        mt: 0.5,
                                        display: 'block',
                                    }}
                                >
                                    {t('emailCannotChange', 'Email cannot be changed')}
                                </Typography>
                            )}
                        </Box>

                        {/* Treatment Duration */}
                        <Box>
                            <label
                                style={{
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    marginBottom: '8px',
                                    display: 'block',
                                }}
                            >
                                {t('treatmentDuration', 'Treatment Duration')} *
                            </label>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl sx={{ flex: 2 }}>
                                    <Select
                                        value={formData.durationValue || ''}
                                        onChange={handleChange('durationValue')}
                                        disabled={isSubmitting}
                                        displayEmpty
                                        sx={{
                                            bgcolor: '#314158',
                                            borderRadius: 1,
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB' },
                                            '&.Mui-disabled': { bgcolor: '#2a3a4d' },
                                            '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    bgcolor: '#314158',
                                                    '& .MuiMenuItem-root': {
                                                        color: 'rgba(255, 255, 255, 0.9)',
                                                        '&:hover': { bgcolor: '#3d4e63' },
                                                        '&.Mui-selected': {
                                                            bgcolor: '#2563EB',
                                                            '&:hover': { bgcolor: '#1D4ED8' },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>{t('enterDuration', 'Enter duration')}</em>
                                        </MenuItem>
                                        {Array.from({ length: 6 }, (_, i) => i + 1).map((n) => (
                                            <MenuItem key={n} value={n.toString()}>
                                                {n}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl sx={{ flex: 1 }}>
                                    <Select
                                        value={formData.durationType}
                                        onChange={handleChange('durationType')}
                                        disabled={isSubmitting}
                                        sx={{
                                            bgcolor: '#314158',
                                            borderRadius: 1,
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'transparent',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#2563EB',
                                            },
                                            '&.Mui-disabled': {
                                                bgcolor: '#2a3a4d',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    bgcolor: '#314158',
                                                    '& .MuiMenuItem-root': {
                                                        color: 'rgba(255, 255, 255, 0.9)',
                                                        '&:hover': {
                                                            bgcolor: '#3d4e63',
                                                        },
                                                        '&.Mui-selected': {
                                                            bgcolor: '#2563EB',
                                                            '&:hover': {
                                                                bgcolor: '#1D4ED8',
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="weeks">{t('weeks', 'Weeks')}</MenuItem>
                                        <MenuItem value="months">{t('months', 'Months')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {formData.durationValue && (
                                <Box sx={{ mt: 1 }}>
                                    <label
                                        style={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        {formData.durationType === 'weeks'
                                            ? t('approxMonths', '{{count}} month(s)', { count: Math.floor(parseInt(formData.durationValue) / 4) })
                                            : t('approxWeeks', '{{count}} week(s)', { count: parseInt(formData.durationValue) * 4 })
                                        }
                                    </label>
                                </Box>
                            )}
                        </Box>

                        {/* Selected Exercises Display */}
                        {selectedExercises.length > 0 && (
                            <Box>
                                <label
                                    style={{
                                        color: 'white',
                                        fontSize: '0.875rem',
                                        marginBottom: '8px',
                                        display: 'block',
                                    }}
                                >
                                    {t('selectedExercises', 'Selected Exercises')} ({selectedExercises.length})
                                </label>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedExercises.map((exercise) => (
                                        <Chip
                                            key={exercise.id}
                                            label={exercise.title}
                                            onDelete={() => handleRemoveExercise(exercise.id)}
                                            disabled={isSubmitting}
                                            sx={{
                                                bgcolor: '#45556c',
                                                color: 'white',
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    '&:hover': {
                                                        color: 'white',
                                                    },
                                                },
                                                '&.Mui-disabled': {
                                                    opacity: 0.6,
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Add Exercises Button */}
                        <Button
                            fullWidth
                            startIcon={<AddIcon />}
                            onClick={() => setVideoLibraryOpen(true)}
                            disabled={isSubmitting}
                            sx={{
                                bgcolor: '#45556c',
                                color: 'white',
                                py: 1.5,
                                borderRadius: 1,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '1rem',
                                '&:hover': {
                                    bgcolor: '#3d4e63',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#2a3a4d',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                },
                            }}
                        >
                            {selectedExercises.length > 0 ? t('modifyExercises', 'Modify Exercises') : t('addExercises', 'Add Exercises')}
                        </Button>
                    </Box>
                </DialogContent>

                {/* Action Buttons */}
                <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
                    <Button
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        sx={{
                            flex: 1,
                            bgcolor: '#45556c',
                            color: 'white',
                            py: 1.5,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            '&:hover': {
                                bgcolor: '#3d4e63',
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#2a3a4d',
                                color: 'rgba(255, 255, 255, 0.5)',
                            },
                        }}
                    >
                        {t('cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            flex: 1,
                            bgcolor: '#2563EB',
                            color: 'white',
                            py: 1.5,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            '&:hover': {
                                bgcolor: '#1D4ED8',
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#1e3a8a',
                                color: 'rgba(255, 255, 255, 0.5)',
                            },
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                {isEditMode ? t('updating', 'Updating...') : t('creating', 'Creating...')}
                            </>
                        ) : (
                            isEditMode ? t('updatePatient', 'Update Patient') : t('createPatient', 'Create Patient')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Video Library Dialog */}
            <VideoLibrarySelection
                open={videoLibraryOpen}
                onClose={() => setVideoLibraryOpen(false)}
                onConfirm={handleExercisesConfirm}
                preSelectedVideos={selectedExercises}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PatientForm;